"use client";
import Link from "next/link";
import { ArrowRight, FileText, Briefcase, Building2, Target, ListChecks } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { demoForm as data } from "@/lib/data/demo";

const accent = "var(--color-amber)";

export function DemoForm() {
  return (
    <Section id="demo-form" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-amber)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <Reveal delay={100}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-lg" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                  <FileText className="h-4 w-4" style={{ color: accent }} />
                </span>
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Form Fields</span>
              </div>
              <div className="mt-4 space-y-1.5">
                {data.fields.map((f, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-sm">
                    <span className="font-semibold" style={{ color: accent }}>{f.field}</span>
                    <span className="text-[rgb(var(--fg-muted))]">— {f.type}</span>
                    <span className={`eyebrow-tiny ${f.required === "Yes" ? "text-[var(--color-crimson)]" : "text-[rgb(var(--fg-subtle))]"}`}>{f.required}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-lg" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                  <Briefcase className="h-4 w-4" style={{ color: accent }} />
                </span>
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Role Options</span>
              </div>
              <ul className="mt-4 space-y-1.5">
                {data.roleOptions.map((r, i) => (
                  <li key={i} className="text-sm text-[rgb(var(--fg-muted))]">{r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          <Reveal delay={200}>
            <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" style={{ color: accent }} />
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Industry</span>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-[rgb(var(--fg-muted))]">
                {data.industryOptions.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={220}>
            <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4" style={{ color: accent }} />
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Organization Size</span>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-[rgb(var(--fg-muted))]">
                {data.sizeOptions.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={240}>
            <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4" style={{ color: accent }} />
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Primary Interest</span>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-[rgb(var(--fg-muted))]">
                {data.interestOptions.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={400}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
